import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Alert,
  IconButton,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Paper,
  Popover,
  RadioGroup,
  Radio,
  InputAdornment,
  Grid,
  Switch,
  MenuItem
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { sv } from 'date-fns/locale';
import { addDays } from 'date-fns';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import PercentIcon from '@mui/icons-material/Percent';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import CloseIcon from '@mui/icons-material/Close';
import config from '../../config/config';
import { useSnackbar } from 'notistack';

interface Discount {
  type: 'percentage' | 'amount';
  value: number;
  description?: string;
}

interface InvoiceItem {
  description: string;
  quantity: number;
  price: number;
  isRot: boolean;
  isRut: boolean;
  discount?: Discount;
  articleNumber?: string;
  material_cost?: number;
  other_costs?: number;
  unit: 'st' | 'h';
}

interface InvoiceFormData {
  customer_name: string;
  customer_email: string;
  customer_address: string;
  customer_postal_code: string;
  customer_city: string;
  customer_personal_number: string;
  delivery_address: string;
  delivery_postal_code: string;
  delivery_city: string;
  due_date: Date;
  items: InvoiceItem[];
  property_designation: string;
  housing_association_number: string;
  apartment_number: string;
  work_period_start: Date | null;
  work_period_end: Date | null;
  work_address: string;
  work_description: string;
  useDeliveryAddress: boolean;
  showExtraFields: boolean;
}

interface CreateInvoiceProps {
  open: boolean;
  onClose: () => void;
  onInvoiceCreated: () => void;
}

const EMPTY_ITEM: InvoiceItem = {
  description: '',
  quantity: 0,
  price: 0,
  isRot: false,
  isRut: false,
  unit: 'st'
};

const ROT_DEDUCTION_PERCENTAGE = 0.5; // 50% ROT-avdrag
const RUT_DEDUCTION_PERCENTAGE = 0.5; // 50% RUT-avdrag

const CreateInvoice: React.FC<CreateInvoiceProps> = ({ open, onClose, onInvoiceCreated }) => {
  const [formData, setFormData] = useState<InvoiceFormData>({
    customer_name: '',
    customer_email: '',
    customer_address: '',
    customer_postal_code: '',
    customer_city: '',
    customer_personal_number: '',
    delivery_address: '',
    delivery_postal_code: '',
    delivery_city: '',
    due_date: addDays(new Date(), 30),
    items: [{ description: '', quantity: 1, price: 0, isRot: false, isRut: false, unit: 'st' }],
    property_designation: '',
    housing_association_number: '',
    apartment_number: '',
    work_period_start: null,
    work_period_end: null,
    work_address: '',
    work_description: '',
    useDeliveryAddress: false,
    showExtraFields: false
  });

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [discountAnchorEl, setDiscountAnchorEl] = useState<null | HTMLElement>(null);
  const [activeDiscountIndex, setActiveDiscountIndex] = useState<number | null>(null);
  const [tempDiscount, setTempDiscount] = useState<Discount>({ type: 'percentage', value: 0 });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  const calculateItemTotal = (item: InvoiceItem) => {
    let total = item.quantity * item.price;
    
    // Applicera rabatt om den finns
    if (item.discount && item.discount.value > 0) {
      if (item.discount.type === 'percentage') {
        total = total * (1 - item.discount.value / 100);
      } else {
        total = total - item.discount.value;
      }
    }

    // Lägg på moms (25%)
    const totalInclVat = total * 1.25;

    // Om det är ROT/RUT, beräkna avdraget på priset inklusive moms
    if (item.isRot) {
      return totalInclVat - (totalInclVat * ROT_DEDUCTION_PERCENTAGE);
    } else if (item.isRut) {
      return totalInclVat - (totalInclVat * RUT_DEDUCTION_PERCENTAGE);
    }

    // Om det inte är ROT/RUT, returnera priset inklusive moms
    return totalInclVat;
  };

  const calculateTotal = () => {
    return formData.items.reduce((total, item) => total + calculateItemTotal(item), 0);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleDateChange = (field: keyof InvoiceFormData) => (date: Date | null) => {
    if (date) {
      setFormData(prev => ({
        ...prev,
        [field]: date
      }));
    }
  };

  const handleItemChange = (index: number, field: keyof InvoiceItem, value: string | number | boolean) => {
    setFormData(prev => {
      const newItems = [...prev.items];
      newItems[index] = {
        ...newItems[index],
        [field]: value
      };
      return {
        ...prev,
        items: newItems
      };
    });
  };

  const handleDiscountClick = (event: React.MouseEvent<HTMLElement>, index: number) => {
    setDiscountAnchorEl(event.currentTarget);
    setActiveDiscountIndex(index);
    const currentDiscount = formData.items[index].discount;
    setTempDiscount(currentDiscount || { type: 'percentage', value: 0 });
  };

  const handleDiscountClose = () => {
    setDiscountAnchorEl(null);
    setActiveDiscountIndex(null);
    setTempDiscount({ type: 'percentage', value: 0 });
  };

  const handleDiscountApply = () => {
    if (activeDiscountIndex !== null) {
      setFormData(prev => {
        const newItems = [...prev.items];
        newItems[activeDiscountIndex] = {
          ...newItems[activeDiscountIndex],
          discount: tempDiscount.value > 0 ? { ...tempDiscount } : undefined
        };
        return {
          ...prev,
          items: newItems
        };
      });
    }
    handleDiscountClose();
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { ...EMPTY_ITEM }]
    }));
  };

  const removeItem = (index: number) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    if (!formData.customer_address) {
      setError('Gatuadress måste anges');
      setLoading(false);
      return;
    }

    if (!formData.customer_postal_code) {
      setError('Postnummer måste anges');
      setLoading(false);
      return;
    }

    if (!formData.customer_city) {
      setError('Ort måste anges');
      setLoading(false);
      return;
    }

    if (!formData.due_date) {
      setError('Förfallodatum måste anges');
      setLoading(false);
      return;
    }

    // Kontrollera om någon post har ROT eller RUT
    const hasRotRut = formData.items.some(item => item.isRot || item.isRut);
    
    // Om ROT/RUT är valt, kontrollera att arbetsperiod är ifylld
    if (hasRotRut && (!formData.work_period_start || !formData.work_period_end)) {
      setError('Arbetsperiod måste anges för ROT/RUT-arbeten');
      setLoading(false);
      return;
    }

    if (formData.items.length === 0 || formData.items.some(item => !item.description || item.quantity <= 0 || item.price <= 0)) {
      setError('Alla fakturaposter måste ha beskrivning, antal och pris');
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${config.api.baseUrl}/invoices`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          amount: calculateTotal(),
          due_date: formData.due_date.toISOString().split('T')[0]
        })
      });

      if (!response.ok) {
        throw new Error('Kunde inte skapa faktura');
      }

      onInvoiceCreated();
      onClose();
      setFormData({
        customer_name: '',
        customer_email: '',
        customer_address: '',
        customer_postal_code: '',
        customer_city: '',
        customer_personal_number: '',
        delivery_address: '',
        delivery_postal_code: '',
        delivery_city: '',
        due_date: addDays(new Date(), 30),
        items: [{ description: '', quantity: 1, price: 0, isRot: false, isRut: false, unit: 'st' }],
        property_designation: '',
        housing_association_number: '',
        apartment_number: '',
        work_period_start: null,
        work_period_end: null,
        work_address: '',
        work_description: '',
        useDeliveryAddress: false,
        showExtraFields: false
      });
    } catch (error) {
      setError('Ett fel uppstod när fakturan skulle skapas');
    } finally {
      setLoading(false);
    }
  };

  const formatDiscount = (discount?: Discount) => {
    if (!discount || discount.value <= 0) return '';
    const discountValue = discount.type === 'percentage' 
      ? `${discount.value}%`
      : new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(discount.value);
    const description = discount.description ? ` - ${discount.description}` : '';
    return ` (${discountValue}${description})`;
  };

  const handlePreview = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${config.api.baseUrl}/invoices/preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customer_name: formData.customer_name,
          customer_address: formData.customer_address,
          customer_postal_code: formData.customer_postal_code,
          customer_city: formData.customer_city,
          customer_personal_number: formData.customer_personal_number,
          delivery_address: formData.delivery_address,
          delivery_postal_code: formData.delivery_postal_code,
          delivery_city: formData.delivery_city,
          property_designation: formData.property_designation,
          housing_association_number: formData.housing_association_number,
          apartment_number: formData.apartment_number,
          work_period_start: formData.work_period_start,
          work_period_end: formData.work_period_end,
          work_address: formData.work_address,
          due_date: formData.due_date,
          items: formData.items
        })
      });

      if (!response.ok) {
        throw new Error('Kunde inte generera förhandsgranskning');
      }

      // Konvertera svaret till en blob
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      // Uppdatera state med URL:en
      setPreviewUrl(url);
      setPreviewOpen(true);
    } catch (error) {
      console.error('Fel vid förhandsgranskning:', error);
      enqueueSnackbar('Kunde inte generera förhandsgranskning', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={sv}>
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            overflowX: 'hidden',
            maxWidth: { xs: '100%', sm: '600px', md: '900px' }
          }
        }}
      >
        <DialogTitle>Skapa ny faktura</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ overflowX: 'hidden', p: { xs: 1, sm: 2, md: 3 } }}>
            <Stack spacing={3} sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
              {error && <Alert severity="error">{error}</Alert>}
              
              <Grid container spacing={4}>
                {/* Vänster sida - Fakturaadress */}
                <Grid item xs={6}>
                  <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                    Fakturaadress
                  </Typography>
                  <Stack spacing={2}>
                    <TextField
                      fullWidth
                      label="Kundnamn"
                      name="customer_name"
                      value={formData.customer_name}
                      onChange={handleChange}
                      required
                    />
                    <TextField
                      fullWidth
                      label="E-post"
                      name="customer_email"
                      type="email"
                      value={formData.customer_email}
                      onChange={handleChange}
                      required
                    />
                    <TextField
                      fullWidth
                      label="Gatuadress"
                      name="customer_address"
                      value={formData.customer_address}
                      onChange={handleChange}
                      required
                    />
                    <Grid container spacing={2}>
                      <Grid item xs={5}>
                        <TextField
                          fullWidth
                          label="Postnummer"
                          name="customer_postal_code"
                          value={formData.customer_postal_code}
                          onChange={handleChange}
                          required
                        />
                      </Grid>
                      <Grid item xs={7}>
                        <TextField
                          fullWidth
                          label="Ort"
                          name="customer_city"
                          value={formData.customer_city}
                          onChange={handleChange}
                          required
                        />
                      </Grid>
                    </Grid>
                  </Stack>
                </Grid>

                {/* Höger sida - Leveransadress */}
                <Grid item xs={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Leveransadress
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.useDeliveryAddress}
                          onChange={handleChange}
                          name="useDeliveryAddress"
                          size="small"
                        />
                      }
                      label="Annan leveransadress"
                    />
                  </Box>
                  <Stack spacing={2}>
                    {formData.useDeliveryAddress ? (
                      <>
                        <TextField
                          fullWidth
                          label="Leveransadress"
                          name="delivery_address"
                          value={formData.delivery_address}
                          onChange={handleChange}
                        />
                        <Grid container spacing={2}>
                          <Grid item xs={5}>
                            <TextField
                              fullWidth
                              label="Postnummer"
                              name="delivery_postal_code"
                              value={formData.delivery_postal_code}
                              onChange={handleChange}
                            />
                          </Grid>
                          <Grid item xs={7}>
                            <TextField
                              fullWidth
                              label="Ort"
                              name="delivery_city"
                              value={formData.delivery_city}
                              onChange={handleChange}
                            />
                          </Grid>
                        </Grid>
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Samma som fakturaadress
                      </Typography>
                    )}
                  </Stack>
                </Grid>
              </Grid>

              <Box sx={{ mb: 3 }}>
                <DatePicker
                  label="Förfallodatum"
                  value={formData.due_date}
                  onChange={handleDateChange('due_date')}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true
                    }
                  }}
                />
              </Box>

              <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
                <Typography variant="h6" gutterBottom>
                  Fakturaposter
                </Typography>
                <Stack spacing={2} sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
                  {formData.items.map((item, index) => (
                    <Paper key={index} elevation={2} sx={{ 
                      p: 2,
                      mb: 2,
                      maxWidth: '100%', 
                      overflowX: 'hidden',
                      backgroundColor: '#f0f2f5',
                      borderRadius: 2,
                      '& .MuiFormControl-root': {
                        maxWidth: '100%'
                      }
                    }}>
                      <Stack spacing={2} sx={{ width: '100%' }}>
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'start',
                          width: '100%',
                          flexWrap: 'nowrap',
                          mb: 1
                        }}>
                          <TextField
                            required
                            label="Beskrivning"
                            value={item.description}
                            onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                            fullWidth
                            sx={{ mr: 2, minWidth: 0 }}
                          />
                          <IconButton 
                            onClick={() => removeItem(index)}
                            disabled={formData.items.length === 1}
                            color="error"
                            sx={{ flexShrink: 0 }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>

                        <Box sx={{ 
                          display: 'flex',
                          gap: 2,
                          width: '100%',
                          mb: 1
                        }}>
                          <TextField
                            required
                            label="Antal"
                            type="number"
                            value={item.quantity || ''}
                            onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value))}
                            inputProps={{ min: 0, step: "0.5" }}
                            sx={{ width: '120px' }}
                          />
                          <TextField
                            select
                            label="Enhet"
                            value={item.unit}
                            onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                            sx={{ width: '120px' }}
                          >
                            <MenuItem value="st">st</MenuItem>
                            <MenuItem value="h">h</MenuItem>
                          </TextField>
                          <TextField
                            required
                            label="Pris (SEK)"
                            type="number"
                            value={item.price || ''}
                            onChange={(e) => handleItemChange(index, 'price', parseFloat(e.target.value))}
                            inputProps={{ min: 0, step: "0.01" }}
                            sx={{ width: '150px' }}
                          />
                        </Box>

                        <Box sx={{ 
                          display: 'flex',
                          gap: 2,
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          pt: 1,
                          borderTop: '1px solid #e0e0e0'
                        }}>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<PercentIcon />}
                            onClick={(e) => handleDiscountClick(e, index)}
                            color={item.discount ? 'primary' : 'inherit'}
                          >
                            Rabatt{formatDiscount(item.discount)}
                          </Button>
                          <Box sx={{ 
                            display: 'flex', 
                            gap: 1,
                            flexWrap: 'nowrap',
                            alignItems: 'center'
                          }}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={item.isRot}
                                  onChange={(e) => {
                                    handleItemChange(index, 'isRot', e.target.checked);
                                    if (e.target.checked) handleItemChange(index, 'isRut', false);
                                  }}
                                />
                              }
                              label="ROT"
                            />
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={item.isRut}
                                  onChange={(e) => {
                                    handleItemChange(index, 'isRut', e.target.checked);
                                    if (e.target.checked) handleItemChange(index, 'isRot', false);
                                  }}
                                />
                              }
                              label="RUT"
                            />
                          </Box>
                        </Box>

                        {(item.isRot || item.isRut) && (
                          <Box sx={{ mt: 1, width: '100%', overflowX: 'hidden' }}>
                            <Grid container spacing={2} sx={{ width: '100%', m: 0 }}>
                              <Grid item xs={6}>
                                <DatePicker
                                  label="Arbete påbörjat"
                                  value={formData.work_period_start}
                                  onChange={handleDateChange('work_period_start')}
                                  slotProps={{
                                    textField: {
                                      fullWidth: true,
                                      required: true
                                    }
                                  }}
                                />
                              </Grid>
                              <Grid item xs={6}>
                                <DatePicker
                                  label="Arbete avslutat"
                                  value={formData.work_period_end}
                                  onChange={handleDateChange('work_period_end')}
                                  slotProps={{
                                    textField: {
                                      fullWidth: true,
                                      required: true
                                    }
                                  }}
                                />
                              </Grid>
                              <Grid item xs={12}>
                                <TextField
                                  fullWidth
                                  label="Personnummer"
                                  name="customer_personal_number"
                                  value={formData.customer_personal_number}
                                  onChange={handleChange}
                                  required
                                  helperText="ÅÅÅÅMMDD-XXXX"
                                />
                              </Grid>
                              
                              {item.isRot ? (
                                <>
                                  <Grid item xs={12}>
                                    <TextField
                                      fullWidth
                                      label="Fastighetsbeteckning"
                                      name="property_designation"
                                      value={formData.property_designation}
                                      onChange={handleChange}
                                      required
                                    />
                                  </Grid>
                                  <Grid item xs={6}>
                                    <TextField
                                      fullWidth
                                      label="Bostadsrättsförening org.nr"
                                      name="housing_association_number"
                                      value={formData.housing_association_number}
                                      onChange={handleChange}
                                    />
                                  </Grid>
                                  <Grid item xs={6}>
                                    <TextField
                                      fullWidth
                                      label="Lägenhetsnummer"
                                      name="apartment_number"
                                      value={formData.apartment_number}
                                      onChange={handleChange}
                                    />
                                  </Grid>
                                </>
                              ) : null}
                            </Grid>
                          </Box>
                        )}
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
                <Button
                  startIcon={<AddIcon />}
                  onClick={addItem}
                  sx={{ mt: 2 }}
                >
                  Lägg till post
                </Button>
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Avbryt</Button>
            <Button
              startIcon={<VisibilityIcon />}
              onClick={handlePreview}
              disabled={loading}
            >
              Förhandsgranska
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
            >
              {loading ? 'Skapar...' : 'Skapa faktura'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={previewOpen}
        onClose={handleClosePreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { minHeight: '80vh' }
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography>Förhandsgranskning av faktura</Typography>
            <Box>
              <IconButton
                onClick={() => {
                  if (previewUrl) {
                    const link = document.createElement('a');
                    link.href = previewUrl;
                    link.download = `faktura-${formData.customer_name}.pdf`;
                    link.click();
                  }
                }}
                title="Ladda ner PDF"
              >
                <DownloadIcon />
              </IconButton>
              <IconButton
                onClick={() => {
                  const iframe = document.querySelector('iframe[name="pdf-preview"]') as HTMLIFrameElement;
                  if (iframe?.contentWindow) {
                    iframe.contentWindow.print();
                  }
                }}
                title="Skriv ut"
              >
                <PrintIcon />
              </IconButton>
              <IconButton onClick={handleClosePreview} title="Stäng">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {previewUrl && (
            <Box sx={{ width: '100%', height: 'calc(80vh - 100px)' }}>
              <object
                data={previewUrl}
                type="application/pdf"
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none'
                }}
              >
                <p>Din webbläsare kan inte visa PDF:er direkt. Klicka på nedladdningsknappen för att öppna filen.</p>
              </object>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Popover
        open={Boolean(discountAnchorEl)}
        anchorEl={discountAnchorEl}
        onClose={handleDiscountClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, width: 300 }}>
          <Typography variant="subtitle1" gutterBottom>
            Lägg till rabatt
          </Typography>
          <RadioGroup
            value={tempDiscount.type}
            onChange={(e) => setTempDiscount(prev => ({ ...prev, type: e.target.value as 'percentage' | 'amount' }))}
          >
            <FormControlLabel value="percentage" control={<Radio />} label="Procentrabatt" />
            <FormControlLabel value="amount" control={<Radio />} label="Beloppsrabatt" />
          </RadioGroup>
          <TextField
            fullWidth
            label={tempDiscount.type === 'percentage' ? 'Rabatt (%)' : 'Rabatt (SEK)'}
            type="number"
            value={tempDiscount.value || ''}
            onChange={(e) => setTempDiscount(prev => ({ ...prev, value: parseFloat(e.target.value) || 0 }))}
            InputProps={{
              endAdornment: tempDiscount.type === 'percentage' ? (
                <InputAdornment position="end">%</InputAdornment>
              ) : undefined
            }}
            sx={{ mt: 2 }}
          />
          <TextField
            fullWidth
            label="Beskrivning av rabatt"
            value={tempDiscount.description || ''}
            onChange={(e) => setTempDiscount(prev => ({ ...prev, description: e.target.value }))}
            sx={{ mt: 2 }}
            placeholder="T.ex. 'Kampanjrabatt' eller 'Mängdrabatt'"
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 1 }}>
            <Button onClick={handleDiscountClose}>Avbryt</Button>
            <Button variant="contained" onClick={handleDiscountApply}>
              Applicera
            </Button>
          </Box>
        </Box>
      </Popover>
    </LocalizationProvider>
  );
};

export default CreateInvoice; 